// Plan 06-05 Task 3 — AuditPage.
//
// Viewer read-only do hash-chained audit log. Backend whitelist-render via
// GET /api/admin/audit (Task 4). Frontend renderiza apenas colunas conhecidas
// — NUNCA importa decryptPayload nem renderiza ciphertext (D-150 ADM-04).
// CI grep enforce.

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Input, Table, Thead, Tbody, Tr, Th, Td } from '@aions/ui-primitives';
import { PHASE1_EVENT_TYPES, PHASE2_EVENT_TYPES } from '@aions/core';
import { apiFetch } from '../lib/api-client';
import { rotuloEvento, descricaoEvento } from '../lib/audit-eventos';

interface AuditEntryView {
  seq: number;
  entryHash: string;
  prevHash: string;
  eventType?: string;
  caseId?: string;
  createdWeek?: string;
  /** ADR-0019 §7 — epoch-ms do ato. AUSENTE (não zero) quando não há ator
   *  identificado: atos do denunciante anônimo ficam só com a semana. */
  createdAtEpochMs?: number;
  userId?: string;
  /** ADR-0009 — quem PRATICOU, quando distinto do sujeito. Era gravado e descartado
   *  pela projeção; sem ele a tela dizia "Autorizou o acesso" sem dizer quem (24/08). */
  actorId?: string;
  requestId?: string;
  motivoHash?: string;
  signerRole?: string;
  expiresAt?: number;
  cofreDenunciaId?: string;
  recursoVoto?: string;
  decisaoKind?: string;
}

/**
 * 🔴 DERIVADO DO CATÁLOGO, nunca mais uma lista à mão (relato do operador, 2026-08-24).
 *
 * Relato literal: _"não estou vendo as solicitações de visualizar denúncia e nem as
 * liberações do cofre de emergência no log de auditoria"_.
 *
 * A causa era esta constante: **16 tipos escritos à mão** contra **101 no catálogo real**
 * (`PHASE1_EVENT_TYPES` + `PHASE2_EVENT_TYPES`). Faltavam TODOS os `acesso_*` — o pedido de
 * visualização, as duas autorizações, a recusa, a concessão e a entrega pelo escrow — e
 * também eventos corriqueiros como `denuncia_aberta`, `policy_accepted` e
 * `conflito_declarado`.
 *
 * ⚠️ A lista era só do FILTRO: as entradas apareciam na tabela. Mas sem poder filtrar por
 * elas, achar um `acesso_autorizado` no meio de 35 `login_success` é procurar agulha — e o
 * efeito prático para quem audita é o mesmo de não existir.
 *
 * É o antipadrão da allow-list escrita à mão que o CLAUDE.md registra: ela nasce completa e
 * envelhece calada. Todo evento novo (os cinco de acesso são de 23–24/08) nascia invisível ao
 * filtro, sem ninguém perceber. Derivando do catálogo, o próximo nasce visível por
 * construção — é o `tsc` que garante, não a memória de quem adicionou.
 */
const EVENT_TYPES: ReadonlyArray<string> = [
  '',
  ...[...PHASE1_EVENT_TYPES, ...PHASE2_EVENT_TYPES].slice().sort((a, b) => a.localeCompare(b)),
];

const PAGE_SIZE = 50;

/* O log guarda SÓ `userId` — nunca e-mail. É desenho, não lacuna: a trilha é
   content-free e o payload é whitelist (D-150/ADM-04). Então o filtro por
   e-mail é resolvido no CLIENTE: a lista de usuários dá o par id↔e-mail, a UI
   fala e-mail e a API continua recebendo `actorId`. */
interface AuditUser {
  id: string;
  email: string;
}

/**
 * ADR-0019 §7 — formata o epoch do ato em horário de Brasília.
 *
 * Fuso FIXO (`America/Sao_Paulo`), não o do navegador: a trilha é prova para
 * fiscalização e dois auditores em máquinas diferentes têm de ler o MESMO
 * horário para o mesmo evento. Deixar no fuso local faria a tela mudar de
 * resposta conforme quem abre — e é o mesmo defeito que o LT-10 fechou no PDF,
 * onde o `CreationDate` carregava o fuso da máquina.
 *
 * Devolve `null` — nunca uma data de fallback — quando o ato não tem hora. Um
 * `new Date(undefined ?? 0)` renderizaria "01/01/1970", que é um valor plausível
 * o bastante para alguém ler como dado real.
 */
function formatarDataHora(epochMs: number | undefined): string | null {
  if (typeof epochMs !== 'number' || !Number.isFinite(epochMs)) return null;
  // `<= 0` é rejeitado explicitamente, e não por acaso: 0 é um epoch VÁLIDO para
  // o JS (01/01/1970) e é justamente o valor que o ADR-0019 §7.5 proíbe gravar.
  // Se um 0 chegar aqui — coluna corrompida, bug num sítio de append, adaptador
  // futuro que preencha default — renderizar "1970" seria o fallback plausível
  // que a decisão inteira existe para impedir: ausência disfarçada de dado.
  if (epochMs <= 0) return null;
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(new Date(epochMs));
}

export function AuditPage(): JSX.Element {
  const { t } = useTranslation();
  const [entries, setEntries] = useState<AuditEntryView[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const [filterActor, setFilterActor] = useState('');
  const [filterRequest, setFilterRequest] = useState('');
  const [filterDenuncia, setFilterDenuncia] = useState('');
  const [filterEvent, setFilterEvent] = useState('');

  const load = useCallback(async (): Promise<void> => {
    try {
      const params = new URLSearchParams();
      params.set('limit', String(PAGE_SIZE));
      params.set('offset', String(offset));
      if (filterActor) params.set('actorId', filterActor);
      if (filterRequest) params.set('requestId', filterRequest);
      if (filterDenuncia) params.set('denunciaId', filterDenuncia);
      if (filterEvent) params.set('eventType', filterEvent);

      const resp = await apiFetch(`/api/admin/audit?${params.toString()}`);
      if (!resp.ok) {
        setError(t('common.error_unknown'));
        return;
      }
      const data = (await resp.json()) as {
        entries: AuditEntryView[];
        total: number;
      };
      setEntries(data.entries);
      setTotal(data.total);
      setError(null);
    } catch {
      setError(t('common.error_network'));
    }
  }, [offset, filterActor, filterRequest, filterDenuncia, filterEvent, t]);

  useEffect(() => {
    void load();
  }, [load]);

  // Lista de usuários só para o par id↔e-mail. Falha em silêncio de propósito:
  // o filtro é conveniência, e um erro aqui não pode esconder a trilha nem
  // sujar a tela com um alerta sobre algo que não é a auditoria.
  const [auditUsers, setAuditUsers] = useState<AuditUser[]>([]);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const resp = await apiFetch('/api/admin/users');
        if (!resp.ok) return;
        const data = (await resp.json()) as { users: AuditUser[] };
        if (!cancelled) setAuditUsers(data.users);
      } catch {
        /* sem lista → o select fica só com "todos" e a coluna mostra o id cru */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const emailById = new Map(auditUsers.map((u) => [u.id, u.email]));
  const usersByEmail = [...auditUsers].sort((a, b) => a.email.localeCompare(b.email, 'pt-BR'));

  function applyFilters(e: React.FormEvent): void {
    e.preventDefault();
    setOffset(0);
    void load();
  }

  function nextPage(): void {
    if (offset + PAGE_SIZE < total) setOffset(offset + PAGE_SIZE);
  }
  function prevPage(): void {
    if (offset > 0) setOffset(Math.max(0, offset - PAGE_SIZE));
  }

  return (
    <section>
      <h1>{t('audit.title')}</h1>

      <p className="cf-field__help">{t('audit.whitelist_notice')}</p>

      {error && (
        <Alert variant="error" style={{ marginBottom: 16 }}>
          {error}
        </Alert>
      )}

      <form className="filter-bar" onSubmit={applyFilters} aria-label="Filtros">
        {/* Era Input de texto livre: o admin tinha que saber o ULID de cor.
            Agora é seleção por e-mail; o valor enviado segue sendo o id, que é
            o que a trilha registra. */}
        <select
          className="cf-input"
          value={filterActor}
          onChange={(e) => setFilterActor(e.target.value)}
          aria-label={t('audit.filter.actor')}
          data-testid="audit-filter-actor"
        >
          <option value="">{t('audit.filter.all_actors')}</option>
          {usersByEmail.map((u) => (
            <option key={u.id} value={u.id}>
              {u.email}
            </option>
          ))}
        </select>
        <Input
          placeholder={t('audit.filter.request')}
          aria-label={t('audit.filter.request')}
          value={filterRequest}
          onChange={(e) => setFilterRequest(e.target.value)}
        />
        <Input
          placeholder={t('audit.filter.denuncia')}
          aria-label={t('audit.filter.denuncia')}
          value={filterDenuncia}
          onChange={(e) => setFilterDenuncia(e.target.value)}
        />
        <select
          className="cf-input"
          value={filterEvent}
          onChange={(e) => setFilterEvent(e.target.value)}
          aria-label={t('audit.filter.event')}
        >
          {EVENT_TYPES.map((ev) => (
            <option key={ev} value={ev} title={descricaoEvento(ev)}>
              {ev === '' ? t('audit.filter.all_events') : rotuloEvento(ev)}
            </option>
          ))}
        </select>
        <Button type="submit">{t('audit.filter.event')}</Button>
      </form>

      <Table>
        <Thead>
          <Tr>
            <Th>{t('audit.column.seq')}</Th>
            <Th>{t('audit.column.event')}</Th>
            <Th>{t('audit.column.case_id')}</Th>
            <Th title={t('audit.column.ator_ajuda')}>{t('audit.column.ator')}</Th>
            <Th title={t('audit.column.sujeito_ajuda')}>{t('audit.column.sujeito')}</Th>
            <Th>{t('audit.column.request_id')}</Th>
            <Th>{t('audit.column.created_week')}</Th>
            <Th title={t('audit.hora_ajuda')}>{t('audit.column.data_hora')}</Th>
            <Th>{t('audit.column.entry_hash')}</Th>
            <Th>{t('audit.column.prev_hash')}</Th>
          </Tr>
        </Thead>
        <Tbody>
          {entries.length === 0 ? (
            <Tr>
              <Td colSpan={10}>{t('common.no_data')}</Td>
            </Tr>
          ) : (
            entries.map((e) => (
              <Tr key={e.seq}>
                <Td mono>{e.seq}</Td>
                <Td title={descricaoEvento(e.eventType ?? '')}>
                  {e.eventType === undefined ? '-' : rotuloEvento(e.eventType)}
                </Td>
                <Td mono>{e.caseId ?? '-'}</Td>
                {/* E-mail quando conhecido; id cru quando NÃO (usuário apagado
                    ou lista indisponível) — nunca "-" no lugar de um id que
                    existe. O id fica no `title` para cruzar com SQL. */}
                {/* 🔴 ATOR e SUJEITO em colunas SEPARADAS (operador, 24/08).
                    Antes só o sujeito aparecia, então "Autorizou o acesso" nunca dizia QUEM
                    autorizou — e num ato que exige duas contas distintas é exatamente essa a
                    informação que torna o controle auditável em vez de declarado.
                    Sem ator distinto a coluna mostra em-dash: o ato é da própria pessoa
                    (um login, por exemplo), e um '-' ali sugeriria dado faltando. */}
                <Td mono title={e.actorId ?? undefined}>
                  {e.actorId ? (emailById.get(e.actorId) ?? e.actorId) : '—'}
                </Td>
                <Td mono title={e.userId ?? undefined}>
                  {e.userId ? (emailById.get(e.userId) ?? e.userId) : '-'}
                </Td>
                {/* A requisição TRUNCA para caber (12 de 36 chars do uuid), então o valor cheio
                    vai no `title` — mesmo motivo das duas colunas acima. Sem isso o auditor vê
                    `df2470de-11f…` e não tem como cruzar com o SQL nem com o export: um id
                    truncado sem a fonte é indistinguível de um id que ele não reconhece.
                    É a requisição que prova que duas autorizações são da MESMA rodada. */}
                <Td mono title={e.requestId ?? undefined}>
                  {e.requestId ? `${e.requestId.slice(0, 12)}…` : '-'}
                </Td>
                <Td>{e.createdWeek ?? '-'}</Td>
                {/* Ausência de hora é INFORMAÇÃO, não buraco: significa "ato sem
                    autor identificado". Por isso um rótulo nomeado com
                    explicação no `title`, e não o mesmo "-" que as outras
                    colunas usam para "não se aplica" — senão "sem hora por
                    desenho" ficaria indistinguível de "hora perdida por bug",
                    que é o que o ADR-0019 §7.5 manda evitar. */}
                <Td mono={formatarDataHora(e.createdAtEpochMs) !== null}>
                  {formatarDataHora(e.createdAtEpochMs) ?? (
                    <span
                      style={{ opacity: 0.6, fontStyle: 'italic' }}
                      title={t('audit.hora_ausente_ajuda')}
                    >
                      {t('audit.hora_ausente')}
                    </span>
                  )}
                </Td>
                <Td mono>{e.entryHash.slice(0, 16)}…</Td>
                <Td mono>{e.prevHash.slice(0, 16)}…</Td>
              </Tr>
            ))
          )}
        </Tbody>
      </Table>

      <div style={{ display: 'flex', gap: 8, marginTop: 16, alignItems: 'center' }}>
        <Button variant="ghost" size="sm" onClick={prevPage} disabled={offset === 0}>
          {t('common.back')}
        </Button>
        <span>
          {String(offset + 1)}–{String(Math.min(offset + PAGE_SIZE, total))} / {String(total)}
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={nextPage}
          disabled={offset + PAGE_SIZE >= total}
          aria-label={t('common.next')}
        >
          →
        </Button>
      </div>
    </section>
  );
}
